import React from 'react';
import i18n from '../../../../../../i18n';
import { METRICS, fmt } from '../../../../data/metrics';
import type { DriverBucket, DriverDimension } from '../../../../types';
import { DrillCruxCallout } from '../DrillCruxCallout';
import {
  bucketSharePct,
  dimFmt,
  fmtPctChange,
  driverTakeaway,
  higherIsBetter,
  isMutedBucket,
  mutedBucketTitle,
  outlierTone,
  shareColor,
  sourceColor,
} from './driverUtils';
import { avgWords, bucketWords, dimHeadline, pctWords } from './driverWords';

/**
 * Every bucket as a table with named columns: the group, the metric in
 * plain words, the compare period, the paired metric, and the reservations
 * behind the row; the venue's own average closes the table for a rate or an
 * average, so "above" and "below" have something to be read against.
 */
function BucketTable({
  dim,
  metricKey,
  fmtVal,
  fmtVal2,
  primaryLabel,
  secondaryLabel,
  compareLabel,
}: {
  dim: DriverDimension;
  metricKey?: string;
  fmtVal: (v: number) => string;
  fmtVal2?: (v: number) => string;
  primaryLabel: string;
  secondaryLabel?: string;
  compareLabel?: string;
}) {
  const paired = dim.buckets.some(b => b.value2 != null) && !!fmtVal2;
  const hasCompare = dim.buckets.some(b => b.compareValue != null);
  const topLabel = dim.topDriver?.label;
  const bottomLabel = dim.bottomDriver?.label;
  const rate = dim.mode === 'rate';
  const words = (b: DriverBucket) => bucketWords(dim, b, fmtVal, metricKey);
  const secondaryWords = (v: number): string => {
    if (rate) return pctWords(v);
    return fmtVal2 ? fmtVal2(v) : String(v);
  };
  const avgOf = (v: number | undefined | null): string => {
    if (v == null) return '';
    if (rate) return pctWords(v);
    return avgWords(dim, v, fmtVal, metricKey);
  };
  const showAverage = dim.mode !== 'share' && dim.baseline != null && dim.baseline > 0;
  return (
    <div className="dcrux-table-wrap">
      <table className="dcrux-table">
        <thead>
          <tr>
            <th scope="col" />
            <th scope="col">{primaryLabel}</th>
            {hasCompare ? (
              <th scope="col">{compareLabel ?? i18n.t('performance:drillUi.compare')}</th>
            ) : null}
            {paired && secondaryLabel ? (
              <th scope="col">{secondaryLabel}</th>
            ) : null}
            <th scope="col">{i18n.t('performance:drillUi.colReservations')}</th>
          </tr>
        </thead>
        <tbody>
          {dim.buckets.map(b => {
            const muted = isMutedBucket(b);
            const mutedTitle = mutedBucketTitle(b);
            const cls = [
              b.label === topLabel ? 'is-top' : '',
              b.label === bottomLabel ? 'is-bottom' : '',
              muted ? 'is-muted' : '',
            ]
              .filter(Boolean)
              .join(' ');
            return (
              <tr key={b.label} className={cls || undefined} title={mutedTitle || undefined}>
                <th scope="row">{b.label}</th>
                <td title={dimFmt(dim, b.value, fmtVal)}>{words(b)}</td>
                {hasCompare ? (
                  <td
                    title={
                      b.compareValue != null ? dimFmt(dim, b.compareValue, fmtVal) : undefined
                    }
                  >
                    {b.compareValue != null
                      ? words({ ...b, value: b.compareValue, sharePct: null })
                      : ''}
                  </td>
                ) : null}
                {paired && secondaryLabel ? (
                  <td>{b.value2 != null ? secondaryWords(b.value2) : ''}</td>
                ) : null}
                <td>{fmt.num(b.sample)}</td>
              </tr>
            );
          })}
          {showAverage ? (
            <tr className="is-average">
              <th scope="row">{i18n.t('performance:drillUi.rowYourAverage')}</th>
              <td>{avgOf(dim.baseline)}</td>
              {hasCompare ? <td>{avgOf(dim.compareBaseline)}</td> : null}
              {paired && secondaryLabel ? (
                <td>{dim.baseline2 != null ? secondaryWords(dim.baseline2) : ''}</td>
              ) : null}
              <td>{dim.total != null ? fmt.num(dim.total) : ''}</td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

function LeadTimeChips({
  dim,
  fmtVal,
}: {
  dim: DriverDimension;
  fmtVal: (v: number) => string;
}) {
  const top = dim.topDriver?.label;
  const bottom = dim.bottomDriver?.label;
  return (
    <div className="dcrux-chips">
      {dim.buckets.map(b => (
        <span
          key={b.label}
          className={`dcrux-chip${b.label === top ? ' is-top' : ''}${b.label === bottom ? ' is-bottom' : ''}`}
        >
          <span className="dcrux-chip-l">{b.label}</span>
          <span className="dcrux-chip-v">{dimFmt(dim, b.value, fmtVal)}</span>
          {b.compareValue != null ? (
            <span className="dcrux-cmp-sub">
              {dimFmt(dim, b.compareValue, fmtVal)}
              {(() => {
                const d = fmtPctChange(b.value, b.compareValue);
                return d ? ` · ${d}` : '';
              })()}
            </span>
          ) : null}
        </span>
      ))}
    </div>
  );
}

function PartyLadder({
  dim,
  fmtVal,
}: {
  dim: DriverDimension;
  fmtVal: (v: number) => string;
}) {
  const max = Math.max(...dim.buckets.map(b => Math.abs(b.value)), 0.0001);
  const top = dim.topDriver?.label;
  return (
    <div className="dcrux-ladder">
      {dim.buckets.map(b => (
        <div
          key={b.label}
          className={`dcrux-ladder-step${b.label === top ? ' is-top' : ''}`}
        >
          <div
            className="dcrux-ladder-fill"
            style={{ height: `${Math.max(8, (Math.abs(b.value) / max) * 100)}%` }}
          />
          <span className="dcrux-ladder-v">
            {dimFmt(dim, b.value, fmtVal)}
            {b.compareValue != null ? (
              <span className="dcrux-cmp-sub">
                {dimFmt(dim, b.compareValue, fmtVal)}
                {(() => {
                  const d = fmtPctChange(b.value, b.compareValue);
                  return d ? ` · ${d}` : '';
                })()}
              </span>
            ) : null}
          </span>
          <span className="dcrux-ladder-l">{b.label}</span>
        </div>
      ))}
    </div>
  );
}

function WeekStrip({
  dim,
  fmtVal,
}: {
  dim: DriverDimension;
  fmtVal: (v: number) => string;
}) {
  const top = dim.topDriver?.label;
  return (
    <div className="dcrux-week">
      {dim.buckets.map(b => (
        <div
          key={b.label}
          className={`dcrux-week-cell${b.label === top ? ' is-top' : ''}`}
        >
          <span className="dcrux-week-dow">{b.label.slice(0, 3)}</span>
          <span className="dcrux-week-v">
            {dimFmt(dim, b.value, fmtVal)}
            {b.compareValue != null ? (
              <span className="dcrux-cmp-sub">
                {dimFmt(dim, b.compareValue, fmtVal)}
                {(() => {
                  const d = fmtPctChange(b.value, b.compareValue);
                  return d ? ` · ${d}` : '';
                })()}
              </span>
            ) : null}
          </span>
        </div>
      ))}
    </div>
  );
}

function DaypartBands({
  dim,
  fmtVal,
}: {
  dim: DriverDimension;
  fmtVal: (v: number) => string;
}) {
  const top = dim.topDriver?.label;
  const n = Math.max(dim.buckets.length, 1);
  return (
    <div
      className="dcrux-bands"
      style={{ gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))` }}
    >
      {dim.buckets.map(b => (
        <div
          key={b.label}
          className={`dcrux-band${b.label === top ? ' is-top' : ''}`}
        >
          <div className="dcrux-band-l">{b.label}</div>
          <div className="dcrux-band-v">
            {dimFmt(dim, b.value, fmtVal)}
            {b.compareValue != null ? (
              <span className="dcrux-cmp-sub">
                {dimFmt(dim, b.compareValue, fmtVal)}
                {(() => {
                  const d = fmtPctChange(b.value, b.compareValue);
                  return d ? ` · ${d}` : '';
                })()}
              </span>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
}

function SourceChips({
  dim,
  fmtVal,
}: {
  dim: DriverDimension;
  fmtVal: (v: number) => string;
}) {
  const top = dim.topDriver?.label;
  return (
    <div className="dcrux-chips">
      {dim.buckets.map(b => (
        <span
          key={b.label}
          className={`dcrux-chip${b.label === top ? ' is-top' : ''}`}
          style={{ borderColor: sourceColor(b.label) }}
        >
          <span
            className="dcrux-comp-dot"
            style={{ background: sourceColor(b.label) }}
          />
          <span className="dcrux-chip-l">{b.label}</span>
          <span className="dcrux-chip-v">
            {dimFmt(dim, b.value, fmtVal)}
            {b.compareValue != null ? (
              <span className="dcrux-cmp-sub">
                {dimFmt(dim, b.compareValue, fmtVal)}
                {(() => {
                  const d = fmtPctChange(b.value, b.compareValue);
                  return d ? ` · ${d}` : '';
                })()}
              </span>
            ) : null}
          </span>
        </span>
      ))}
    </div>
  );
}

function GuestSplit({
  dim,
  fmtVal,
}: {
  dim: DriverDimension;
  fmtVal: (v: number) => string;
}) {
  const top = dim.topDriver?.label;
  return (
    <div className="dcrux-split">
      {dim.buckets.map(b => (
        <div
          key={b.label}
          className={`dcrux-split-cell${b.label === top ? ' is-top' : ''}`}
        >
          <div className="dcrux-split-l">{b.label}</div>
          <div className="dcrux-split-v">
            {dimFmt(dim, b.value, fmtVal)}
            {b.compareValue != null ? (
              <span className="dcrux-cmp-sub">
                {dimFmt(dim, b.compareValue, fmtVal)}
                {(() => {
                  const d = fmtPctChange(b.value, b.compareValue);
                  return d ? ` · ${d}` : '';
                })()}
              </span>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
}

function ShareComposition({
  dim,
  fmtVal,
}: {
  dim: DriverDimension;
  fmtVal: (v: number) => string;
}) {
  const sorted = [...dim.buckets].sort(
    (a, b) => Math.abs(b.value) - Math.abs(a.value),
  );
  const top = sorted.slice(0, 4);
  const rest = sorted.slice(4);
  const restValue = rest.reduce((a, b) => a + Math.abs(b.value), 0);
  const compareTotal = dim.buckets.reduce(
    (a, b) => a + Math.abs(b.compareValue ?? 0),
    0,
  );
  const cmpPctFor = (label: string): string | null => {
    const b = dim.buckets.find(x => x.label === label);
    if (!b || b.compareValue == null || compareTotal <= 0) return null;
    return `${Math.round((Math.abs(b.compareValue) / compareTotal) * 100)}%`;
  };
  const segments = [
    ...top.map((b, i) => ({
      label: b.label,
      value: Math.abs(b.value),
      color: shareColor(i),
      fmt: dimFmt(dim, b.value, fmtVal),
      pct: bucketSharePct(dim, b.value),
      cmpPct: cmpPctFor(b.label),
    })),
  ];
  if (restValue > 0) {
    segments.push({
      label: i18n.t('performance:drillUi.other'),
      value: restValue,
      color: shareColor(4),
      fmt: dimFmt(dim, restValue, fmtVal),
      pct: bucketSharePct(dim, restValue),
      cmpPct: null,
    });
  }
  const total = segments.reduce((a, s) => a + s.value, 0) || 1;
  const hasCompare = compareTotal > 0;
  return (
    <>
      <div className="dcrux-comp">
        {segments.map(s => (
          <span
            key={s.label}
            className="dcrux-comp-seg"
            style={{
              width: `${(s.value / total) * 100}%`,
              background: s.color,
            }}
            title={`${s.label}: ${s.fmt}`}
          />
        ))}
      </div>
      <div className="dcrux-comp-legend">
        {segments.map(s => (
          <span className="dcrux-comp-item" key={s.label}>
            <span className="dcrux-comp-dot" style={{ background: s.color }} />
            {s.label} · {s.pct}%
            {hasCompare && s.cmpPct ? (
              <span className="dcrux-cmp-sub"> · {s.cmpPct}</span>
            ) : null}
          </span>
        ))}
      </div>
    </>
  );
}

function RateOrAvgCallout({
  dim,
  fmtVal,
}: {
  dim: DriverDimension;
  fmtVal: (v: number) => string;
}) {
  const hib = higherIsBetter(dim);
  const focus = hib ? dim.bottomDriver : dim.topDriver;
  const prefer = hib ? 'bottom' : 'top';
  const tone = outlierTone(dim, focus, prefer);
  const kicker = hib ? i18n.t('performance:drillUi.belowBaseline') : i18n.t('performance:drillUi.aboveBaseline');
  if (!focus) {
    const takeaway = driverTakeaway(dim);
    return takeaway ? (
      <DrillCruxCallout text={takeaway} tone="neutral" />
    ) : null;
  }
  const delta = focus.vsBaselinePct ?? 0;
  const deltaStr =
    dim.mode === 'rate' || dim.unit === 'pct'
      ? i18n.t('performance:drillUi.ptVsBaseline', { delta: `${delta >= 0 ? '+' : ''}${delta}` })
      : i18n.t('performance:drillUi.pctVsBaseline', { delta: `${delta >= 0 ? '+' : ''}${delta}` });
  return (
    <DrillCruxCallout
      kicker={kicker}
      tone={tone}
      text={`${focus.label} · ${dimFmt(dim, focus.value, fmtVal)} · ${deltaStr}`}
    />
  );
}

/** Dimension-appropriate crux visual + shared advanced bucket list. */
export function DriverDimensionCrux({
  dim,
  fmtVal,
  showLabel = true,
  hideGenericCallout = false,
  hideTakeaway = false,
  compact = false,
}: {
  dim: DriverDimension;
  fmtVal: (v: number) => string;
  showLabel?: boolean;
  /** When parent already shows baseline + outlier stats for this dim. */
  hideGenericCallout?: boolean;
  /** Skip the share takeaway box (use in multi-dim correlates). */
  hideTakeaway?: boolean;
  compact?: boolean;
}) {
  const takeaway = driverTakeaway(dim);
  let body: React.ReactNode;
  if (dim.mode === 'share' && (dim.id === 'leadTime' || dim.buckets.length >= 3)) {
    if (dim.id === 'partySize') body = <PartyLadder dim={dim} fmtVal={fmtVal} />;
    else if (dim.id === 'dayOfWeek') body = <WeekStrip dim={dim} fmtVal={fmtVal} />;
    else if (dim.id === 'daypart' || dim.id === 'regime')
      body = <DaypartBands dim={dim} fmtVal={fmtVal} />;
    else if (dim.id === 'source') body = <SourceChips dim={dim} fmtVal={fmtVal} />;
    else if (dim.id === 'guestType') body = <GuestSplit dim={dim} fmtVal={fmtVal} />;
    else if (dim.id === 'leadTime') body = <LeadTimeChips dim={dim} fmtVal={fmtVal} />;
    else body = <ShareComposition dim={dim} fmtVal={fmtVal} />;
  } else if (dim.id === 'partySize') {
    body = <PartyLadder dim={dim} fmtVal={fmtVal} />;
  } else if (dim.id === 'dayOfWeek') {
    body = <WeekStrip dim={dim} fmtVal={fmtVal} />;
  } else if (dim.id === 'daypart' || dim.id === 'regime') {
    body = <DaypartBands dim={dim} fmtVal={fmtVal} />;
  } else if (dim.id === 'source') {
    body = <SourceChips dim={dim} fmtVal={fmtVal} />;
  } else if (dim.id === 'guestType') {
    body = <GuestSplit dim={dim} fmtVal={fmtVal} />;
  } else if (dim.id === 'leadTime') {
    body = <LeadTimeChips dim={dim} fmtVal={fmtVal} />;
  } else if (dim.mode === 'share') {
    body = <ShareComposition dim={dim} fmtVal={fmtVal} />;
  } else if (!hideGenericCallout) {
    body = <RateOrAvgCallout dim={dim} fmtVal={fmtVal} />;
  } else {
    body = null;
  }

  if (!body && (hideTakeaway || !takeaway)) return null;

  return (
    <div className={`dcrux-dim${compact ? ' dcrux-dim-compact' : ''}`}>
      {showLabel && body ? <div className="dcrux-dim-lab">{dim.label}</div> : null}
      {body}
      {takeaway && dim.mode === 'share' && !hideTakeaway ? (
        <DrillCruxCallout text={takeaway} tone="neutral" compact />
      ) : null}
    </div>
  );
}

export function DriverDimensionAdvanced({
  dim,
  metricKey,
  fmtVal,
  fmtVal2,
  secondaryLabel,
  compareLabel,
}: {
  dim: DriverDimension;
  /** The metric the figures speak for: names the column and the noun. */
  metricKey?: string;
  fmtVal: (v: number) => string;
  fmtVal2?: (v: number) => string;
  /** Column name for the paired metric's figures. */
  secondaryLabel?: string;
  compareLabel?: string;
}) {
  const headline = dimHeadline(dim, fmtVal, metricKey);
  const primaryLabel =
    (metricKey && METRICS[metricKey]?.short) || i18n.t('performance:drillUi.colValue');
  return (
    <div className="dcrux-dim">
      <div className="dcrux-dim-lab">{dim.label}</div>
      {headline ? <DrillCruxCallout text={headline} tone="neutral" /> : null}
      <BucketTable
        dim={dim}
        metricKey={metricKey}
        fmtVal={fmtVal}
        fmtVal2={fmtVal2}
        primaryLabel={primaryLabel}
        secondaryLabel={secondaryLabel}
        compareLabel={compareLabel}
      />
    </div>
  );
}
